import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useBlocker, useLocation, useNavigate } from 'react-router-dom';

import { EyeIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import profilesAPI from '@/api/profiles';
import Button, { BUTTON_KIND } from '@/components/Button';
import { CompanyInformation } from '@/components/EditProfilePage/CompanyInfoForm';
import { PersonalInformation } from '@/components/EditProfilePage/PersonalInfoForm';
import { ProfileQuickFileLinks } from '@/components/EditProfilePage/QuickLinksForm';
import { ProfileSocialLinks } from '@/components/EditProfilePage/SocialLinksForm';
import InfoPanelDivider from '@/components/InfoPanelDivider';
import InfoPanelFooter from '@/components/InfoPanelFooter';
import { UnsavedChangesModal } from '@/components/UnsavedChangesPrompt';
import MESSAGES from '@/constants/messages-en';
import useAuth from '@/hooks/useAuth';
import usePreviewProfile from '@/hooks/usePreviewProfile';
import IProfile from '@/types/IProfile';

import { ProfilePreview, ProfilePreviewModal } from '../ProfilePreview';
import { AdditionalContactInfo } from './AdditionalContactInfo';
import { ProfileManagement } from './ProfileManagement';
import { WorkingProfileForm } from './schema';

type ProfileForm = {
  profile: Partial<IProfile>;
};

/**
 * Type for form value getter function (watch or getValues from react-hook-form).
 * Note: Using `any` here is a pragmatic choice because:
 * 1. React Hook Form's watch/getValues support nested paths like 'settings.bg_color'
 * 2. Creating a fully type-safe nested path type would be complex and brittle
 * 3. The return types are implicitly validated by the consuming code (ProfilePreview component)
 * 4. The schema validation ensures runtime type safety
 */
type FormValueGetter = <T = any>(name: string) => T;

/**
 * Builds settings object for preview based on settings level.
 * When settings_level is 'organisation', uses organization settings from preview profile.
 * When settings_level is 'profile', uses form values for profile-specific settings.
 * @param settingsLevel - 'organisation' or 'profile'
 * @param previewProfileSettings - Organization-level settings from preview profile data
 * @param getFormValue - Function to retrieve form values (watch or getValues)
 * @returns Settings object for ProfilePreview component
 */
export function buildPreviewSettings(
  settingsLevel: 'organisation' | 'profile',
  previewProfileSettings: any | undefined,
  getFormValue: FormValueGetter,
) {
  return settingsLevel === 'organisation' && previewProfileSettings
    ? {
        bg_color: previewProfileSettings.bg_color,
        text_color: previewProfileSettings.text_color,
        button_bg_color: previewProfileSettings.button_bg_color,
        button_text_color: previewProfileSettings.button_text_color,
        logo: previewProfileSettings.company_logo || null,
        logo_file_id: previewProfileSettings.company_logo_file_id || null,
      }
    : {
        bg_color: getFormValue('settings.bg_color'),
        text_color: getFormValue('settings.text_color'),
        button_bg_color: getFormValue('settings.button_bg_color'),
        button_text_color: getFormValue('settings.button_text_color'),
        logo: getFormValue('settings.logo'),
        logo_file_id: getFormValue('settings.logo_file_id'),
      };
}

/**
 * Builds profile content object for preview from form values.
 * Always uses current form values to show real-time preview of unsaved changes.
 * @param getFormValue - Function to retrieve form values (watch or getValues)
 * @returns Profile content object for ProfilePreview component
 */
export function buildProfileContent(getFormValue: FormValueGetter) {
  return {
    additional_information: getFormValue('additional_information'),
    first_name: getFormValue('first_name'),
    middle_name: getFormValue('middle_name'),
    last_name: getFormValue('last_name'),
    company_name: getFormValue('company_name'),
    department: getFormValue('department'),
    email: getFormValue('email'),
    mobile_number: getFormValue('mobile_number'),
    job_title: getFormValue('job_title'),
    pronouns: getFormValue('pronouns'),
    photo: getFormValue('photo'),
    company_phone_number: getFormValue('company_phone_number'),
    company_website: getFormValue('company_website'),
    street_address: getFormValue('street_address'),
    city: getFormValue('city'),
    state: getFormValue('state'),
    postCode: getFormValue('post_code'),
    country: getFormValue('country'),
    profile_file_links:
      getFormValue('profile_file_links')?.filter(
        (link: any) => link.status !== 'incomplete',
      ) ?? [],
    profile_quick_links: getFormValue('profile_quick_links'),
    profile_social_links: getFormValue('profile_social_links'),
    note: getFormValue('note'),
    extra_company_phone_numbers: getFormValue('extra_company_phone_numbers'),
  };
}

export function EditProfileForm() {
  const { userRole, orgID } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [, , profileID] = location.pathname.split('/');
  const {
    handleSubmit,
    formState: { isDirty, isSubmitting, isSubmitSuccessful },
  } = useFormContext<WorkingProfileForm>();

  const { mutateAsync: saveProfile } = useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      // Invalidate profile queries to ensure fresh data on next visit
      // Note: profileID must be parsed as int to match the query key in EditProfilePage
      const parsedProfileID = parseInt(profileID, 10);
      queryClient.invalidateQueries({
        queryKey: ['showProfile', parsedProfileID],
      });
      queryClient.invalidateQueries({
        queryKey: ['showProfileWithHashID'],
        exact: false,
      });
    },
    onError: () => {
      toast.error('Profile failed to update. Please try again');
    },
  });

  // Confirms with the user if they want to navigate away if the form is
  // dirty. Does not intercept everything e.g. refreshes or closing the tab
  // but serves to reduce the possibility of a user accidentally losing their
  // progress.
  const blocker = useBlocker(isDirty && !isSubmitSuccessful);

  // Gating on !isSubmitSuccessful above makes the blocker stop blocking nav
  // after a successful submit. However, this can take a little time (there is a
  // render in between when isSubmitSuccessful is true but the blocker is still
  // ready to block), so we also have this useEffect that lets blocked requests
  // through if submit is succesful but the blocker hasn't gotten a chance to
  // stop blocking yet.
  useEffect(() => {
    if (blocker.state === 'blocked' && isSubmitSuccessful) {
      blocker.proceed();
    }
  }, [blocker, isSubmitSuccessful]);

  const isAdmin = userRole === 'org_admin';

  const onInvalid = (errors: Record<string, unknown>) => {
    // Log detailed validation errors for debugging
    console.error('Form validation failed:', errors);
    toast.error('Please review and correct the form to continue');
  };

  async function onSubmit(data: WorkingProfileForm) {
    const profileForm: ProfileForm = {
      profile: {
        additional_information: data.additional_information,
        first_name: data.first_name,
        middle_name: data.middle_name,
        last_name: data.last_name,
        city: data.city,
        state: data.state,
        country: data.country,
        post_code: data.post_code,
        company_name: data.company_name,
        company_website: data.company_website,
        company_phone_number: data.company_phone_number,
        department: data.department,
        email: data.email,
        mobile_number: data.mobile_number,
        extra_company_phone_numbers: data.extra_company_phone_numbers?.filter(
          phone => !!phone,
        ),
        photo_file_id: data.photo?.id || null,
        profile_file_links: data.profile_file_links.filter(
          link => link.status !== 'incomplete',
        ),
        profile_quick_links: data.profile_quick_links,
        profile_social_links: data.profile_social_links,
        note: data.note,
        job_title: data.job_title,
        contact_exchange_enabled: data.contact_exchange_enabled,
        pronouns: data.pronouns,
        street_address: data.street_address,
        settings: data.settings,
        settings_level: data.settings_level,
      },
    };

    await saveProfile(profileForm);

    toast.success(MESSAGES.profile.edit.success);

    navigate('/', {
      state: {
        setupInfo: true,
      },
    });
  }

  async function updateProfile(data: ProfileForm) {
    if (orgID === undefined) {
      return;
    }

    await profilesAPI.updateProfile({
      orgID,
      profileID: parseInt(profileID),
      body: data,
    });
  }

  return (
    <>
      <div className="flex flex-row pb-20 space-x-8">
        <div className="pt-6 max-w-full">
          <PersonalInformation />
          <InfoPanelDivider />
          <ProfileSocialLinks />
          <InfoPanelDivider />
          <CompanyInformation />
          <InfoPanelDivider />
          <ProfileQuickFileLinks />
          <InfoPanelDivider />
          <AdditionalContactInfo />
          <InfoPanelDivider />
          {isAdmin && <ProfileManagement />}
        </div>
        <EditProfilePreview />
        {blocker.state === 'blocked' && (
          <UnsavedChangesModal
            proceed={handleSubmit(onSubmit, onInvalid)}
            cancel={blocker.proceed}
            reset={blocker.reset}
            isLoading={isSubmitting}
          />
        )}
      </div>
      <InfoPanelFooter>
        <div className="flex justify-between xl:justify-end space-x-4">
          <div className="xl:hidden">
            <EditProfilePreviewModal />
          </div>
          <div className="flex justify-end space-x-4">
            <Button
              buttonText="Cancel"
              kind={BUTTON_KIND.WHITE}
              onClick={() => navigate('/')}
            />
            <Button
              buttonText="Save changes"
              onClick={handleSubmit(onSubmit, onInvalid)}
              loading={isSubmitting}
            />
          </div>
        </div>
      </InfoPanelFooter>
    </>
  );
}

function EditProfilePreview() {
  const { watch } = useFormContext<WorkingProfileForm>();
  const { previewProfile } = usePreviewProfile();

  const settingsLevel = watch('settings_level');

  // Build settings and content from current form state for real-time preview
  // Note: watch() subscribes to all form changes, causing re-render on any field change
  // When settings_level is 'organisation', uses organization settings from preview profile
  const settings = buildPreviewSettings(
    settingsLevel,
    previewProfile?.settings,
    watch,
  );
  const profileContent = buildProfileContent(watch);

  return (
    <div
      className="hidden xl:block w-full max-w-[352px] md:max-w-[388px] h-fit sticky top-0 pt-8"
      style={{ zoom: 0.8 }}
    >
      <div className="p-4 rounded-md bg-white shadow-[0_0_0_1px_theme(colors.gray.300)]">
        <ProfilePreview
          content={profileContent}
          settings={settings}
          showDefaultProfilePicture={false}
        />
      </div>
    </div>
  );
}

function EditProfilePreviewModal() {
  const { getValues } = useFormContext<WorkingProfileForm>();
  const { previewProfile } = usePreviewProfile();

  const [isProfilePreviewOpen, setIsProfilePreviewOpen] = useState(false);

  const settingsLevel = getValues('settings_level');

  // Use getValues for one-time read on modal open (doesn't subscribe to changes)
  // When settings_level is 'organisation', uses organization settings from preview profile
  const settings = buildPreviewSettings(
    settingsLevel,
    previewProfile?.settings,
    getValues,
  );
  const profileContent = buildProfileContent(getValues);

  return (
    <>
      <Button
        buttonText="Preview"
        kind={BUTTON_KIND.WHITE}
        icon={<EyeIcon />}
        onClick={() => setIsProfilePreviewOpen(true)}
      />
      <ProfilePreviewModal
        isOpen={isProfilePreviewOpen}
        setIsOpen={setIsProfilePreviewOpen}
        settings={settings}
        content={profileContent}
      />
    </>
  );
}
